/**
 * Form State Manager
 * Single-responsibility class for managing form state: values, errors, touched fields,
 * dirty flag, and submission status. Supports snapshot/restore for undo and testing.
 * Extracted from FormEngine to satisfy SRP.
 */

/**
 * Serializable snapshot of all form state.
 */
export interface FormSnapshot {
  values: Record<string, unknown>;
  errors: Record<string, string>;
  touched: string[];
  dirty: boolean;
  isSubmitting: boolean;
}

/**
 * Owns and manages mutable form state.
 * Properties are accessed directly by FormEngine for performance.
 */
export class FormStateManager {
  /** Field values keyed by control ID */
  values: Record<string, unknown>;
  /** Validation errors keyed by control ID */
  errors: Record<string, string>;
  /** Set of touched/interacted field IDs */
  touched: Set<string>;
  /** Whether any field value has changed since initialization */
  dirty: boolean;
  /** Whether the form is currently being submitted */
  isSubmitting: boolean;

  constructor(initialValues?: Record<string, unknown>) {
    this.values = initialValues ? { ...initialValues } : {};
    this.errors = {};
    this.touched = new Set();
    this.dirty = false;
    this.isSubmitting = false;
  }

  /**
   * Get a shallow copy of current values (safe to expose without mutation risk).
   */
  getValues(): Record<string, unknown> {
    return { ...this.values };
  }

  /**
   * Get a shallow copy of current errors.
   */
  getErrors(): Record<string, string> {
    return { ...this.errors };
  }

  /**
   * Check if a specific field has been touched.
   */
  isTouched(fieldId: string): boolean {
    return this.touched.has(fieldId);
  }

  /**
   * Capture a serializable snapshot of the current state.
   */
  getSnapshot(): FormSnapshot {
    return {
      values: { ...this.values },
      errors: { ...this.errors },
      touched: Array.from(this.touched),
      dirty: this.dirty,
      isSubmitting: this.isSubmitting,
    };
  }

  /**
   * Restore state from a previously captured snapshot.
   */
  restoreSnapshot(snapshot: FormSnapshot): void {
    this.values = { ...snapshot.values };
    this.errors = { ...snapshot.errors };
    this.touched = new Set(snapshot.touched);
    this.dirty = snapshot.dirty;
    this.isSubmitting = snapshot.isSubmitting;
  }

  /**
   * Reset state, optionally overriding initial values.
   */
  reset(initialValues?: Record<string, unknown>): void {
    this.values = initialValues ? { ...initialValues } : {};
    this.errors = {};
    this.touched.clear();
    this.dirty = false;
    this.isSubmitting = false;
  }
}
